import axios from 'axios';
import { getAuthHeaders } from './firebase';
import errors from '../modules/http-errors';
import { getEnvVar } from '../utils/env';
import type { StashCategory, Vendor } from '@stash/shared-types';

interface ApiResponse {
  data: {
    errors?: unknown;
    data?: unknown;
  };
}

class DataService {
  private endPointURL: string;

  constructor() {
    this.endPointURL = '/';

    console.log('[DataService] Configuration:', {
      endPointURL: this.endPointURL,
      emulatorSetting: getEnvVar('VITE_USE_FIREBASE_EMULATOR'),
      remoteHost: getEnvVar('VITE_REMOTE_API_HOST'),
    });
  }

  async getVendor(vendorId: string): Promise<unknown> {
    const vendorEndPointURL = this.endPointURL + `api/vendors/${vendorId}`;
    const headers = await getAuthHeaders();
    const response = await axios.get(vendorEndPointURL, { headers });

    if (response.data.errors) {
      throw response.data.errors;
    }

    const vendor = response.data;
    if (!vendor) {
      throw new errors.ResourceNotFoundError(`No Vendor with id: ${vendorId}`);
    }
    return vendor;
  }

  /**
   * Load the full vendor catalog for the admin vendor-search screen.
   * Hits the admin-only GET /api/vendors endpoint and unwraps the
   * { success, message, data } envelope to the Vendor array.
   */
  async getAllVendors(): Promise<Vendor[]> {
    const vendorsEndPointURL = this.endPointURL + 'api/vendors';
    const headers = await getAuthHeaders();
    const response = await axios.get(vendorsEndPointURL, { headers });
    return this.handleResponse(response) as Vendor[];
  }

  async updateVendor(vendorId: string, updateData: unknown): Promise<unknown> {
    const vendorEndPointURL = this.endPointURL + `api/vendors/${vendorId}`;
    const headers = await getAuthHeaders();
    const response = await axios.put(vendorEndPointURL, updateData, { headers });
    return this.handleResponse(response);
  }

  async uploadVendorImage(
    vendorId: string,
    imageType: string,
    base64ImageData: string,
    fileName: string | null = null,
    mimeType: string | null = null,
  ): Promise<unknown> {
    const uploadEndPointURL = this.endPointURL + `api/vendors/${vendorId}/upload-image`;
    const headers = await getAuthHeaders();

    const requestBody = {
      imageType,
      imageData: base64ImageData,
      fileName: fileName || 'image.jpg',
      mimeType: mimeType || 'image/jpeg',
    };

    const response = await axios.post(uploadEndPointURL, requestBody, { headers });
    const apiResult = this.handleResponse(response);

    return { imageType, action: 'upload', ...(apiResult as object) };
  }

  handleResponse(response: ApiResponse): unknown {
    if (response.data.errors) {
      throw response.data.errors;
    }
    return (response.data as { data?: unknown }).data;
  }

  async deleteVendorImage(vendorId: string, imageType: string, imageUrl?: string | null): Promise<unknown> {
    let deleteEndPointURL = this.endPointURL + `api/vendors/${vendorId}/image/${imageType}`;

    if (imageType === 'photos' && !imageUrl) {
      throw new errors.MissingParameterError('imageUrl is required when deleting photos');
    }

    if (imageUrl) {
      deleteEndPointURL += `?imageUrl=${encodeURIComponent(imageUrl)}`;
    }

    const headers = await getAuthHeaders();
    const response = await axios.delete(deleteEndPointURL, { headers });
    const apiResult = response.data;
    return { imageType, imageUrl, action: 'delete', ...apiResult };
  }

  async getAuthenticatedStasher(): Promise<unknown> {
    const stasherEndPointURL = this.endPointURL + `api/stashers/by-auth`;
    const headers = await getAuthHeaders();
    const response = await axios.get(stasherEndPointURL, { headers });

    if (response.data.errors) {
      throw response.data.errors;
    }

    const stasher = response.data;
    if (!stasher) {
      throw new errors.ResourceNotFoundError('No Stasher matched auth ID');
    }
    return stasher;
  }

  getStashCard(stashCardId: string): Promise<never> {
    return Promise.reject(new Error(`Can't load StashCard ${stashCardId}: not implemented`));
  }

  getCategories(categoryNames: string[]): Promise<unknown> {
    return new Promise((resolve, reject) => {
      axios
        .post(this.endPointURL, {
          query: `
          query CategoryMatchingNames($categoryNames: [String!]){
            category(where: {name: {_in: $categoryNames}}) {
              name
              id
            }
          }`,
          variables: { categoryNames },
        })
        .then((response) => {
          if (response.data.errors) {
            return reject(response.data.errors);
          }
          const categories = response.data.data.category;
          return resolve(categories);
        })
        .catch(reject);
    });
  }

  saveCategories(categories: StashCategory[]): Promise<unknown> {
    return new Promise((resolve, reject) => {
      axios
        .post(this.endPointURL, {
          query: `
          mutation add_categories($categories: [category_insert_input!]!){
            insert_category(objects: $categories) {
              returning {
                id
                name
                parentCategoryId
              }
            }
          }
          `,
          variables: { categories },
        })
        .then((response) => {
          if (response.data.errors) {
            return reject(response.data.errors);
          }
          const savedCategories = response.data.data.insert_category.returning;
          return resolve(savedCategories);
        })
        .catch(reject);
    });
  }

  getStashers(): Promise<unknown> {
    return new Promise((resolve, reject) => {
      axios
        .post(this.endPointURL, {
          query: `
          query AllStashers{
            stasher {
              id
              firstName
              lastName
              email
              mobilePhone
            }
          }`,
        })
        .then((response) => {
          if (response.data.errors) {
            return reject(response.data.errors);
          }
          const stashers = response.data.data.stasher;
          return resolve(stashers);
        })
        .catch(reject);
    });
  }

  saveNewStashers(stashers: unknown[]): Promise<unknown> {
    return new Promise((resolve, reject) => {
      axios
        .post(this.endPointURL, {
          query: `
            mutation add_stashers($stashers: [stasher_insert_input!]!){
              insert_stasher(objects: $stashers) {
                returning {
                  id
                  firstName
                  lastName
                  email
                  mobilePhone
                }
              }
            }
          `,
          variables: { stashers },
        })
        .then((response) => {
          if (response.data.errors) {
            return reject(response.data.errors);
          }
          const savedStashers = response.data.data.insert_stasher.returning;
          return resolve(savedStashers);
        })
        .catch(reject);
    });
  }

  updateStasher(stasher: Record<string, unknown>): Promise<unknown> {
    const stasherId = stasher.id;
    return new Promise((resolve, reject) => {
      axios
        .post(this.endPointURL, {
          query: `
            mutation update_stasher($id: Int, $changes: stasher_set_input) {
              update_stasher(
                where: {id: {_eq: $id}},
                _set: $changes
              ) {
                affected_rows
                returning {
                  id
                  firstName
                  lastName
                  email
                  mobilePhone
                  profilePhotoUrl
                }
              }
            }
          `,
          variables: { id: stasherId, changes: stasher },
        })
        .then((response) => {
          if (response.data.errors) {
            return reject(response.data.errors[0]);
          }
          const updatedStasher = response.data.data.update_stasher.returning[0];
          return resolve(updatedStasher);
        })
        .catch(reject);
    });
  }

  saveVendors(_vendors: unknown[]): Promise<never> {
    return Promise.reject('not implemented.');
  }

  saveStashCards(stashCards: unknown[]): Promise<unknown> {
    return new Promise((resolve, reject) => {
      axios
        .post(this.endPointURL, {
          query: `
            mutation add_stashcards($stashCards: [stashcard_insert_input!]!){
              insert_stashcard(objects: $stashCards) {
                returning {
                  id
                  stasherId
                  vendorId
                }
              }
            }
          `,
          variables: { stashCards },
        })
        .then((response) => {
          if (response.data.errors) {
            return reject(response.data.errors);
          }
          const savedStashCards = response.data.data.insert_stashcard.returning;
          return resolve(savedStashCards);
        })
        .catch(reject);
    });
  }
}

export default DataService;
